import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Upload, X, FileText } from 'lucide-react';
import { useData } from '../context/DataContext';
import { usePricing } from '../context/PricingContext';
import { Product, QuoteRequest } from '../types';
import { calculatePrice, generateItemId } from '../utils/pricing';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { trackEcommerce } from '../utils/analytics';

const Quote: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { products } = useData();
  const { pricingSettings } = usePricing();

  const productId = searchParams.get('product');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [width, setWidth] = useState<number>(24);
  const [height, setHeight] = useState<number>(18);
  const [quantity, setQuantity] = useState<number>(1);
  const [material, setMaterial] = useState<string>('');
  const [finishing, setFinishing] = useState<string>('');
  const [colorSpec, setColorSpec] = useState<string>('');
  const [customOptions, setCustomOptions] = useState<{ [key: string]: string }>({});
  const [customText, setCustomText] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [artworkFile, setArtworkFile] = useState<File | null>(null);
  const [artworkPreview, setArtworkPreview] = useState<string>('');
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [footerHeight, setFooterHeight] = useState(0);
  const [sizeError, setSizeError] = useState<string>('');
  const [areDimensionsValid, setAreDimensionsValid] = useState<boolean>(true);

  // Validate dimensions considering orientation (landscape/portrait)
  const validateDimensions = (w: number, h: number, product: Product): { isValid: boolean; error: string } => {
    if (!product.customizable) {
      return { isValid: true, error: '' };
    }

    const minW = product.minWidth || 1;
    const minH = product.minHeight || 1;
    const maxW = product.maxWidth || 999;
    const maxH = product.maxHeight || 999;

    // Get the smaller and larger constraints
    const smallerMin = Math.min(minW, minH);
    const largerMin = Math.max(minW, minH);
    const smallerMax = Math.min(maxW, maxH);
    const largerMax = Math.max(maxW, maxH);

    // Check if either dimension is too small
    if (w < smallerMin || h < smallerMin) {
      return { 
        isValid: false, 
        error: `Both dimensions must be at least ${smallerMin} inches` 
      };
    }

    // Check if both dimensions exceed the smaller max (both dimensions can't be large)
    if (w > smallerMax && h > smallerMax) {
      return { 
        isValid: false, 
        error: `Only one dimension can exceed ${smallerMax} inches` 
      };
    }

    // Check if any dimension exceeds the larger max
    if (w > largerMax || h > largerMax) {
      return { 
        isValid: false, 
        error: `No dimension can exceed ${largerMax} inches` 
      };
    }

    // Check if at least one dimension meets the larger minimum (for asymmetric constraints)
    if (largerMin > smallerMin && Math.max(w, h) < largerMin) {
      return { 
        isValid: false, 
        error: `At least one dimension must be ${largerMin} inches or larger` 
      };
    }

    return { isValid: true, error: '' };
  };

  // Set up intersection observer for footer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Calculate how much to push up the sticky footer
            const footer = entry.target;
            const stickyFooter = document.querySelector('[data-sticky-footer]');
            
            if (stickyFooter && footer) {
              const stickyRect = stickyFooter.getBoundingClientRect();
              const footerRect = footer.getBoundingClientRect();
              
              // Calculate the overlap
              const viewportHeight = window.innerHeight;
              const footerTop = footerRect.top;
              const stickyBottom = viewportHeight;
              const overlap = stickyBottom - footerTop;
              
              if (overlap > 0) {
                setFooterHeight(overlap);
              } else {
                setFooterHeight(0);
              }
            }
            setIsFooterVisible(true);
          } else {
            setIsFooterVisible(false);
            setFooterHeight(0);
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] // Multiple thresholds for smooth updates
      }
    );

    // Observe the footer
    const setupObserver = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        observer.observe(footer);
      }
    };

    // Try immediately and also after a delay
    setupObserver();
    const timeout = setTimeout(setupObserver, 500);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (productId) {
      const product = products.find(p => p.id === productId);
      if (product) {
        setSelectedProduct(product);
        // Set initial size based on product type
        if (!product.customizable && product.fixedSizes && product.fixedSizes.length > 0) {
          // For fixed size products, use the first available size
          setWidth(product.fixedSizes[0].width);
          setHeight(product.fixedSizes[0].height);
        } else {
          // For customizable products, use default or min sizes
          setWidth(product.defaultWidth || product.minWidth || 24);
          setHeight(product.defaultHeight || product.minHeight || 18);
        }
        setMaterial(product.materials?.[0]?.id || '');
        setFinishing(product.finishingOptions?.[0]?.id || '');
        setColorSpec(product.colorSpecs?.[0]?.id || '');
        setQuantity(product.minQuantity || 1);
        
        // Initialize custom options with first choice or empty
        const initialCustomOptions: { [key: string]: string } = {};
        product.customOptions?.forEach(option => {
          if (option.required && option.options.length > 0) {
            initialCustomOptions[option.id] = option.options[0].id;
          }
        });
        setCustomOptions(initialCustomOptions);
      }
    }
  }, [productId, products]);

  useEffect(() => {
    if (selectedProduct) {
      const request: QuoteRequest = {
        product: selectedProduct,
        width,
        height,
        quantity,
        material,
        finishing,
        colorSpec,
        customOptions,
        customText,
      };
      // Only pass volume discounts if product is eligible
      const volumeDiscounts = selectedProduct.eligibleForVolumeDiscount !== false ? pricingSettings.volumeDiscounts : [];
      setPrice(calculatePrice(request, selectedProduct.materials, selectedProduct.finishingOptions, volumeDiscounts));
    }
  }, [selectedProduct, width, height, quantity, material, finishing, colorSpec, customOptions, customText, pricingSettings.volumeDiscounts]);

  // Validate dimensions whenever they change
  useEffect(() => {
    if (selectedProduct && selectedProduct.customizable) {
      const validation = validateDimensions(width, height, selectedProduct);
      setAreDimensionsValid(validation.isValid);
      setSizeError(validation.error);
    } else {
      setAreDimensionsValid(true);
      setSizeError('');
    }
  }, [width, height, selectedProduct]);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedProduct]);

  useEffect(() => {
    // Scroll to top when component mounts or category changes
    window.scrollTo(0, 0);
  }, [selectedProduct]);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleArtworkUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'image/svg+xml', 'application/postscript'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please upload an image file (JPG, PNG, GIF, SVG), PDF, or AI file');
        return;
      }

      // Validate file size (max 10MB for artwork)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      setArtworkFile(file);

      // Create preview for image files
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setArtworkPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        // For non-image files, clear preview
        setArtworkPreview('');
      }
    }
  };

  const removeArtwork = () => {
    setArtworkFile(null);
    setArtworkPreview('');
  };

  const handleAddToCart = async () => {
    if (!selectedProduct || !areDimensionsValid) return;

    let artworkData = '';
    if (artworkFile) {
      artworkData = await convertFileToBase64(artworkFile);
    }

    const request: QuoteRequest = {
      product: selectedProduct,
      width,
      height,
      quantity,
      material,
      finishing,
      colorSpec,
      customOptions,
      customText,
      uploadedFile: artworkData,
    };

    const cartItem = {
      id: generateItemId(request),
      product: selectedProduct,
      width,
      height,
      quantity,
      material,
      finishing,
      colorSpec,
      customOptions,
      customText,
      uploadedFile: artworkData,
      price: price / quantity, // Price per unit
    };

    addItem(cartItem);
    
    // Track add to cart event
    trackEcommerce.addToCart({
      id: selectedProduct.id,
      name: selectedProduct.name,
      category: selectedProduct.category,
      price: price / quantity,
      quantity: quantity
    });
    
    navigate('/cart');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative">
      <SEO 
        title="Get Instant Quote"
        description="Get an instant quote for custom signs, banners, and printing. Upload your design, choose materials and size. Fast turnaround in Montgomery County."
        keywords="instant quote, sign printing quote, banner quote, custom sign pricing"
      />
      {/* Add padding bottom to account for sticky footer */}
      <div className="container mx-auto px-4 py-8" style={{ paddingBottom: price > 0 && selectedProduct ? '8rem' : '2rem' }}>
        <Breadcrumb 
          items={[
            { label: 'Get Quote' }
          ]}
        />
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Get Instant Quote</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Configure your custom sign and get real-time pricing.<br />Upload your artwork and we'll handle the rest.
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top Left - Product Selection */}
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8 h-full">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-keystone-blue rounded-full flex items-center justify-center text-white font-bold mr-3">1</div>
                  Select Product
                </h2>
                <div className="space-y-4">
                  <select
                    value={selectedProduct?.id || ''}
                    onChange={(e) => {
                      const product = products.find(p => p.id === e.target.value);
                      setSelectedProduct(product || null);
                      if (product) {
                        // Set initial size based on product type
                        if (!product.customizable && product.fixedSizes && product.fixedSizes.length > 0) {
                          // For fixed size products, use the first available size
                          setWidth(product.fixedSizes[0].width);
                          setHeight(product.fixedSizes[0].height);
                        } else {
                          // For customizable products, use default or min sizes
                          setWidth(product.defaultWidth || product.minWidth || 24);
                          setHeight(product.defaultHeight || product.minHeight || 18);
                        }
                        setMaterial(product.materials?.[0]?.id || '');
                        setFinishing(product.finishingOptions?.[0]?.id || '');
                        setColorSpec(product.colorSpecs?.[0]?.id || '');
                        setQuantity(product.minQuantity || 1);
                        
                        // Initialize custom options with first choice or empty
                        const initialCustomOptions: { [key: string]: string } = {};
                        product.customOptions?.forEach(option => {
                          if (option.required && option.options.length > 0) {
                            initialCustomOptions[option.id] = option.options[0].id;
                          }
                        });
                        setCustomOptions(initialCustomOptions);
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold bg-gray-50 text-lg"
                  >
                    <option value="">Choose a product...</option>
                    {products
                      .filter(product => product.active !== false)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {product.category}
                        </option>
                      ))}
                  </select>

                  {selectedProduct && (
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                      <div className="aspect-square bg-gray-200 flex items-center justify-center overflow-hidden relative group">
                        {(() => {
                          const images = selectedProduct.images && selectedProduct.images.length > 0 
                            ? selectedProduct.images 
                            : (selectedProduct.image ? [selectedProduct.image] : []);
                          
                          if (images.length > 0) {
                            return (
                              <>
                                <div className="relative w-full h-full">
                                  {images.map((image, index) => (
                                    <img 
                                      key={index}
                                      src={image} 
                                      alt={`${selectedProduct.name} - Image ${index + 1}`}
                                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                                        index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                                      }`}
                                    />
                                  ))}
                                </div>
                                {images.length > 1 && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                                      }}
                                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                                      aria-label="Previous image"
                                    >
                                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                                      }}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                                      aria-label="Next image"
                                    >
                                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                      {images.map((_, index) => (
                                        <button
                                          key={index}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentImageIndex(index);
                                          }}
                                          className={`w-2 h-2 rounded-full transition-colors ${
                                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                          }`}
                                          aria-label={`Go to image ${index + 1}`}
                                        />
                                      ))}
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          } else {
                            return <span className="text-gray-500">No Image</span>;
                          }
                        })()}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold mb-2">{selectedProduct.name}</h3>
                        <p className="text-gray-600 mb-4">{selectedProduct.description}</p>
                        {selectedProduct.customizable && (
                          <p className="text-sm text-gray-500">
                            Size: {selectedProduct.minWidth}" × {selectedProduct.minHeight}" to {selectedProduct.maxWidth}" × {selectedProduct.maxHeight}"
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Right - Configuration */}
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8 h-full">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-keystone-blue rounded-full flex items-center justify-center text-white font-bold mr-3">2</div>
                  Configure Details
                </h2>

                {selectedProduct ? (
                  <div className="space-y-6">
                    {/* Size Configuration */}
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Size Specifications</h3>
                      {sizeError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">{sizeError}</p>
                        </div>
                      )}
                      {selectedProduct.customizable ? (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Width (inches)
                              </label>
                              <input
                                type="number"
                                value={width}
                                onChange={(e) => setWidth(Number(e.target.value))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Height (inches)
                              </label>
                              <input
                                type="number"
                                value={height}
                                onChange={(e) => setHeight(Number(e.target.value))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                              />
                            </div>
                          </div>
                          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <strong>Total Area:</strong> {((width * height) / 144).toFixed(2)} sq ft
                            </p>
                            {(selectedProduct.minWidth || selectedProduct.minHeight || selectedProduct.maxWidth || selectedProduct.maxHeight) && (
                              <p className="text-xs text-gray-600 mt-2">
                                <strong>Size Range:</strong> {selectedProduct.minWidth || 1}" × {selectedProduct.minHeight || 1}" to {selectedProduct.maxWidth || 999}" × {selectedProduct.maxHeight || 999}" (can be rotated)
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {selectedProduct.fixedSizes && selectedProduct.fixedSizes.length > 0 ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Size
                              </label>
                              <select
                                value={`${width}x${height}`}
                                onChange={(e) => {
                                  const [w, h] = e.target.value.split('x').map(Number);
                                  setWidth(w);
                                  setHeight(h);
                                }}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                              >
                                {selectedProduct.fixedSizes.map((size) => (
                                  <option key={size.id} value={`${size.width}x${size.height}`}>
                                    {size.width}" × {size.height}" - ${size.price.toFixed(2)}{size.note ? ` (${size.note})` : ''}
                                  </option>
                                ))}
                              </select>
                              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                                <p className="text-sm text-gray-700">
                                  <strong>Selected Size:</strong> {width}" × {height}"
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">
                                No sizes have been configured for this product. Please contact us for available options.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity
                        {selectedProduct.minQuantity && selectedProduct.minQuantity > 1 && (
                          <span className="text-sm text-gray-500 ml-2">(Minimum: {selectedProduct.minQuantity})</span>
                        )}
                      </label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(selectedProduct.minQuantity || 1, Number(e.target.value)))}
                        min={selectedProduct.minQuantity || 1}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                      />
                    </div>

                    {/* Material Selection */}
                    {selectedProduct?.materials && selectedProduct.materials.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Material
                        </label>
                        <select
                          value={material}
                          onChange={(e) => setMaterial(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                        >
                          {selectedProduct.materials.map(mat => (
                            <option key={mat.id} value={mat.id}>
                              {mat.name} {
                                mat.pricingType === 'multiplier' && mat.priceMultiplier !== 1
                                  ? `(${mat.priceMultiplier! > 1 ? '+' : ''}${((mat.priceMultiplier! - 1) * 100).toFixed(0)}%)`
                                  : mat.pricingType === 'fixed' && mat.fixedCost !== 0
                                    ? `(+$${mat.fixedCost!.toFixed(2)}/sq ft)`
                                    : ''
                              }
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Finishing Options */}
                    {selectedProduct?.finishingOptions && selectedProduct.finishingOptions.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Finishing Options
                        </label>
                        <select
                          value={finishing}
                          onChange={(e) => setFinishing(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                        >
                          {selectedProduct.finishingOptions.map(option => {
                            const squareFeet = (width * height) / 144;
                            const priceDisplay = option.price === 0 ? '' :
                              option.pricingType === 'perSqFt' 
                                ? `(+$${(option.price * squareFeet).toFixed(2)})`
                                : `(+$${option.price.toFixed(2)})`;
                            return (
                              <option key={option.id} value={option.id}>
                                {option.name} {priceDisplay}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                    {/* Color Specification */}
                    {selectedProduct?.colorSpecs && selectedProduct.colorSpecs.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Color Specification
                        </label>
                        <select
                          value={colorSpec}
                          onChange={(e) => setColorSpec(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                        >
                          {selectedProduct.colorSpecs.map(spec => {
                            const squareFeet = (width * height) / 144;
                            const totalPriceChange = spec.price * squareFeet;
                            const priceDisplay = totalPriceChange === 0 ? '' :
                              totalPriceChange > 0 ? `(+$${totalPriceChange.toFixed(2)})` :
                                `($${totalPriceChange.toFixed(2)})`;
                            return (
                              <option key={spec.id} value={spec.id}>
                                {spec.name} {priceDisplay}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                    {/* Custom Options */}
                    {selectedProduct?.customOptions && selectedProduct.customOptions.map(option => (
                      <div key={option.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {option.name} {option.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={customOptions[option.id] || ''}
                          onChange={(e) => setCustomOptions({ ...customOptions, [option.id]: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                          required={option.required}
                        >
                          {option.required && !customOptions[option.id] && (
                            <option value="" disabled>Select {option.name}</option>
                          )}
                          {option.options.map(choice => {
                            const squareFeet = (width * height) / 144;
                            let priceDisplay = '';
                            
                            if (choice.pricingType === 'flat' && choice.flatCost) {
                              priceDisplay = choice.flatCost > 0 ? `(+$${choice.flatCost.toFixed(2)})` : '';
                            } else if (choice.pricingType === 'fixed' && choice.fixedCost) {
                              const totalCost = choice.fixedCost * squareFeet;
                              priceDisplay = totalCost > 0 ? `(+$${totalCost.toFixed(2)})` : '';
                            } else if (choice.pricingType === 'multiplier' && choice.priceMultiplier && choice.priceMultiplier !== 1) {
                              priceDisplay = `(×${choice.priceMultiplier})`;
                            }
                            
                            return (
                              <option key={choice.id} value={choice.id}>
                                {choice.name} {priceDisplay}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    ))}

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-keystone-gold focus:border-keystone-gold text-lg"
                        placeholder="Enter any notes or special instructions..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <p className="text-xl font-medium text-gray-500 mb-2">No Product Selected</p>
                    <p className="text-gray-400 max-w-xs">Please select a product from Step 1 to configure size, material, and finishing options</p>
                  </div>
                )}
              </div>
            </div>

            {/* Artwork Upload - Full Width */}
            <div className="lg:col-span-2">
              {selectedProduct && (
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-keystone-blue rounded-full flex items-center justify-center text-white font-bold mr-3">3</div>
                    Upload Artwork
                  </h2>

                  <div>
                    {artworkFile && (
                      <div className="p-6 bg-green-50 rounded-xl border-2 border-green-300 mb-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            {artworkPreview ? (
                              <img
                                src={artworkPreview}
                                alt="Artwork preview"
                                className="w-20 h-20 object-cover rounded-lg border-2 border-white shadow-lg"
                              />
                            ) : (
                              <div className="w-20 h-20 bg-gray-200 rounded-lg border-2 border-white shadow-lg flex items-center justify-center">
                                <FileText className="h-10 w-10 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-gray-900">{artworkFile.name}</p>
                              <p className="text-sm text-gray-600">
                                {(artworkFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                              <p className="text-sm text-green-600 font-medium">✓ File uploaded successfully</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeArtwork}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Upload Area */}
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-12 h-12 mb-4 text-gray-400" />
                            <p className="mb-2 text-lg font-medium text-gray-700">
                              <span className="text-keystone-blue font-semibold">Click to upload</span> your artwork
                            </p>
                            <p className="text-sm text-gray-500">
                              JPG, PNG, GIF, SVG, PDF, or AI (MAX. 10MB)
                            </p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.gif,.svg,.pdf,.ai"
                            onChange={handleArtworkUpload}
                          />
                        </label>
                      </div>

                      {/* Artwork Guidelines */}
                      <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg h-64 flex flex-col justify-center">
                        <p className="font-medium mb-4 text-gray-900 text-lg">Artwork Guidelines:</p>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start">
                            <span className="text-keystone-gold mr-2">•</span>
                            <span>High resolution images (300 DPI minimum)</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-keystone-gold mr-2">•</span>
                            <span>Vector files (SVG, AI) preferred for best quality</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-keystone-gold mr-2">•</span>
                            <span>Ensure text is readable at the final print size</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-keystone-gold mr-2">•</span>
                            <span>Include bleed area for full-coverage designs</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Sticky Footer Quote Summary */}
      {price > 0 && selectedProduct && (
        <div 
          data-sticky-footer
          className="fixed left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40 transition-all duration-300"
          style={{
            bottom: isFooterVisible ? `${footerHeight}px` : '0px',
          }}
        >
          <div className="container mx-auto px-4 py-3 md:py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              {/* Product info - Hidden on small mobile */}
              <div className="hidden sm:block text-center sm:text-left">
                <p className="text-sm text-gray-600 line-clamp-1">
                  {selectedProduct.name} • {width}" × {height}" • Qty: {quantity}
                </p>
              </div>

              {/* Price info - Centered */}
              <div className="flex items-center gap-4 sm:gap-6 flex-1 justify-center">
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-600">Per Unit</p>
                    <p className="text-base sm:text-lg font-semibold">${(price / quantity).toFixed(2)}</p>
                  </div>
                  <div className="h-8 w-px bg-gray-300"></div>
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-600">Total</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">${price.toFixed(2)}</p>
                  </div>
                  {(() => {
                    // Check for volume discount
                    if (selectedProduct.eligibleForVolumeDiscount !== false) {
                      const appliedDiscount = pricingSettings.volumeDiscounts
                        .sort((a, b) => b.minQuantity - a.minQuantity)
                        .find(rule => quantity >= rule.minQuantity);
                      
                      if (appliedDiscount && appliedDiscount.discount > 0) {
                        return (
                          <>
                            <div className="h-8 w-px bg-gray-300"></div>
                            <div className="text-center">
                              <p className="text-xs sm:text-sm text-green-600 font-medium">Discount</p>
                              <p className="text-base sm:text-lg font-bold text-green-600">
                                {(appliedDiscount.discount * 100).toFixed(0)}% off
                              </p>
                            </div>
                          </>
                        );
                      }
                    }
                    return null;
                  })()}
              </div>

              {/* Add to cart button */}
              <button
                onClick={handleAddToCart}
                disabled={!areDimensionsValid}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-bold text-base sm:text-lg transition-all duration-200 shadow-lg whitespace-nowrap ${
                  areDimensionsValid
                    ? 'bg-gradient-to-br from-yellow-400 via-keystone-gold to-orange-500 hover:from-yellow-500 hover:via-keystone-gold-600 hover:to-orange-600 text-keystone-blue hover:shadow-xl cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quote;